import type {TourOperatorDiscountCampaign} from '@/modules/common/hooks/useCampaigns/types';

import {useBreakpointUp} from '@travel-frontend/core';
import {
  CampaignBanner,
  Skeleton,
  type CampaignBannerProps,
} from '@travel-frontend/ui-components';
import {noop} from 'lodash';
import {useCallback, useEffect} from 'react';

import {CashbackCampaignBanner} from '@/components/CashbackCampaignBanner';
import {CombinedCampaignBanner} from '@/components/CombinedCampaignBanner';
import {GenericBanner} from '@/components/GenericBanner';
import {SeasonalCashbackCampaignBanner} from '@/components/SeasonalCashbackCampaignBanner';
import {TourOperatorDiscountCampaignBanner} from '@/components/TourOperatorDiscountCampaignBanner';
import {CrossBorderBanner} from '@/modules/common/components/CrossBorderBanner';
import {NewsletterBanner} from '@/modules/common/components/NewsletterBanner';
import {
  useCashbackCampaign,
  useCombinedCampaign,
  useTourOperatorDiscountCampaign,
  useUpliftAds,
} from '@/modules/common/hooks';
import {useCrossBorderBannerSplit} from '@/modules/common/hooks/splits/useCrossBorderBannerSplit';
import {useNewsletterBannerSplit} from '@/modules/common/hooks/splits/useNewsletterBannerSplit';
import {
  useCampaignBanner,
  useGenericBanner,
} from '@/modules/common/hooks/useCampaigns';
import {useTracking} from '@/modules/common/hooks/useTracking';
import {UpliftAds} from '@/modules/hotels/components/List/components/UpliftAds/UpliftAds';
import {useAppSelector} from '@/redux/config';
import {selectAreHotelsLoading} from '@/redux/v1/hotels/list/reducer';

export const Banner = () => {
  const newsletterBannerSplit = useNewsletterBannerSplit();
  const discountCampaignBanner = useTourOperatorDiscountCampaign();
  const cashbackCampaignBanner = useCashbackCampaign();
  const combinedCampaignBanner = useCombinedCampaign();
  const areHotelsLoading = useAppSelector(selectAreHotelsLoading);
  const {dispatchGenericTrackingEvent} = useTracking();
  const crossBorderBannerSplit = useCrossBorderBannerSplit();
  const campaignBanner = useCampaignBanner();
  const genericBanner = useGenericBanner();

  const handleTourOperatorDiscountCampaignBannerClick = useCallback(
    (campaign: TourOperatorDiscountCampaign) => {
      discountCampaignBanner.handleTourOperatorDiscountCampaignBannerClick(
        campaign
      );
    },
    [discountCampaignBanner]
  );

  const upliftAds = useUpliftAds(
    import.meta.env.VITE_UPLIFT_ADS_NATIVE_TOP_ZONE_ID
  );

  const isLoadingData =
    areHotelsLoading ||
    upliftAds.isLoading ||
    discountCampaignBanner.isLoadingCampaigns ||
    cashbackCampaignBanner.isLoadingCampaigns ||
    campaignBanner.isLoadingCampaigns ||
    genericBanner.isLoadingCampaigns;

  useEffect(() => {
    if (
      !upliftAds.shouldShowBanner &&
      !cashbackCampaignBanner.shouldShowBanner &&
      discountCampaignBanner.shouldShowBanner
    ) {
      dispatchGenericTrackingEvent({
        event: 'view_promotion',
        event_action: 'promotion_banner_loaded_last_minute',
        non_interaction: true,
        ecommerce: {
          promotion_name: 'Last Minute',
          creative_name: discountCampaignBanner.campaign?.titles.lg,
        },
      });
    } else if (
      !upliftAds.shouldShowBanner &&
      cashbackCampaignBanner.shouldShowBanner &&
      !discountCampaignBanner.shouldShowBanner
    ) {
      dispatchGenericTrackingEvent({
        event: 'promotion_banner',
        event_action: 'cashback_loaded',
        ad_position: '1',
        non_interaction: true,
      });
    }
  }, [
    discountCampaignBanner.shouldShowBanner,
    discountCampaignBanner.campaign,
    dispatchGenericTrackingEvent,
    upliftAds.shouldShowBanner,
    cashbackCampaignBanner.shouldShowBanner,
  ]);

  const mdUp = useBreakpointUp('md');
  const lgUp = useBreakpointUp('lg');

  let bgImage: CampaignBannerProps['backgroundImage'] = undefined;
  if (campaignBanner?.campaign?.backgroundImage) {
    bgImage = {
      url: campaignBanner.campaign.backgroundImage.sm,
    };

    if (lgUp) {
      bgImage.url = campaignBanner.campaign.backgroundImage.lg;
    } else if (mdUp) {
      bgImage.url = campaignBanner.campaign.backgroundImage.md;
    }
  }

  let banner = null;

  if (isLoadingData) {
    banner = <Skeleton variant="rect" height="100px" />;
  } else if (crossBorderBannerSplit.enabled && crossBorderBannerSplit.config) {
    banner = (
      <CrossBorderBanner
        version={crossBorderBannerSplit.config.version}
        url={crossBorderBannerSplit.config.url}
        airports={crossBorderBannerSplit.config.airports}
        price={crossBorderBannerSplit.config.priceTotal}
        priceEnabled={crossBorderBannerSplit.config.priceEnabled}
      />
    );
  } else if (upliftAds.shouldShowBanner) {
    banner = <UpliftAds advertisements={upliftAds.advertisement} />;
  } else if (campaignBanner.shouldShowBanner) {
    banner = (
      <CampaignBanner
        onClick={noop}
        title={campaignBanner.campaign.title}
        subtitle={campaignBanner.campaign.subTitle}
        badgeText={campaignBanner.campaign.badgeText}
        preset={campaignBanner.campaign.preset}
        backgroundImage={bgImage}
        link={{
          href: campaignBanner.campaign.url,
          target: '_blank',
          rel: 'noopener noreferrer',
        }}
        legalHint={{
          text: campaignBanner.campaign.legalHint,
          tooltip: campaignBanner.campaign.legalTooltip,
        }}
        textColor={campaignBanner.campaign.textColor}
      />
    );
  } else if (genericBanner.shouldShowBanner) {
    banner = (
      <GenericBanner
        conditionsId={genericBanner.campaign.conditionsId}
        backgroundImage={genericBanner.campaign.backgrounds}
        badgeImage={genericBanner.campaign.graphics}
        link={genericBanner.campaign.links}
        title={genericBanner.campaign.title}
        subtitle={genericBanner.campaign.subtitle}
      />
    );
  } else if (combinedCampaignBanner.shouldShowBanner) {
    banner = (
      <CombinedCampaignBanner
        campaign={combinedCampaignBanner.combinedCampaign}
        discountCampaign={combinedCampaignBanner.discountCampaign}
        onClick={
          combinedCampaignBanner.handleTourOperatorDiscountCampaignBannerClick
        }
      />
    );
  } else if (cashbackCampaignBanner.shouldShowBanner) {
    banner = cashbackCampaignBanner.campaign.seasonal ? (
      <SeasonalCashbackCampaignBanner
        {...cashbackCampaignBanner.campaign}
        position={1}
      />
    ) : (
      <CashbackCampaignBanner
        {...cashbackCampaignBanner.campaign}
        position={1}
      />
    );
  } else if (discountCampaignBanner.shouldShowBanner) {
    banner = (
      <TourOperatorDiscountCampaignBanner
        onClick={handleTourOperatorDiscountCampaignBannerClick}
        {...discountCampaignBanner.campaign}
      />
    );
  } else if (newsletterBannerSplit.enabled) {
    banner = <NewsletterBanner treatment={newsletterBannerSplit.treatment} />;
  }

  return banner;
};
